import { motion } from "framer-motion";
import { ArrowRight, ShoppingCart, BarChart, Smartphone, Package, Zap } from "lucide-react";
import SEO from "../components/SEO";

export default function POSSystem() {
    return (
        <div className="min-h-screen bg-base text-primary">
            <SEO
                title="Best POS System & Point of Sale Software | Omnichannel Retail"
                description="blinqbooks POS is the best point of sale system for retail & restaurants in 2026. Omnichannel inventory sync, offline mode, mobile POS, smart analytics & real-time sales tracking. Try free."
            />

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold uppercase mb-6">
                            <Zap className="w-4 h-4" />
                            Omnichannel Retail
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight">
                            Unified Sales, <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-yellow-500">Everywhere</span>
                        </h1>
                        <p className="text-lg text-secondary dark:text-gray-300 mb-8 max-w-lg leading-relaxed">
                            Connect your physical store to your digital world. blinqbooks POS syncs inventory, sales, and customers across all channels instantly.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button className="btn-primary w-full sm:w-auto px-8 py-4 shadow-lg text-lg">
                                Upgrade Your Store
                            </button>
                            <button className="btn-secondary w-full sm:w-auto px-8 py-4 shadow-sm text-lg">
                                Retail Hardware
                            </button>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-amber-500/10 blur-[100px] rounded-full"></div>
                        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl p-6 overflow-hidden">
                            <div className="flex gap-4 mb-6">
                                <div className="flex-1 bg-slate-500/10 dark:bg-white/5 p-4 rounded-xl">
                                    <div className="text-xs text-secondary dark:text-gray-400 mb-1">Today's Sales</div>
                                    <div className="text-xl font-bold dark:text-white font-mono">₹4,821.50</div>
                                </div>
                                <div className="flex-1 bg-amber-500/10 dark:bg-amber-500/20 p-4 rounded-xl text-amber-600 dark:text-amber-400">
                                    <div className="text-xs mb-1">Stock Alerts</div>
                                    <div className="text-xl font-bold font-mono">12 Items</div>
                                </div>
                            </div>
                            <div className="space-y-3 opacity-60">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="flex justify-between items-center py-2">
                                        <div className="h-2 w-24 bg-gray-200 dark:bg-gray-600 rounded"></div>
                                        <div className="h-2 w-12 bg-gray-200 dark:bg-gray-600 rounded"></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Detail */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            {[
                                { icon: <Package className="text-blue-500" />, title: "Inventory Autopilot", desc: "Never oversell again. Inventory updates in real-time across your website and store." },
                                { icon: <Smartphone className="text-purple-500" />, title: "Sell on Mobile", desc: "Take orders and payments on any mobile device, anywhere on the floor." },
                                { icon: <ShoppingCart className="text-emerald-500" />, title: "Offline Mode", desc: "Keep selling even when the internet goes out. Data syncs when you're back online." },
                            ].map((f, i) => (
                                <div key={i} className="flex gap-6">
                                    <div className="p-3 bg-slate-500/10 dark:bg-white/5 rounded-xl h-fit">{f.icon}</div>
                                    <div>
                                        <h3 className="text-xl font-bold dark:text-white mb-2">{f.title}</h3>
                                        <p className="text-secondary dark:text-gray-400 leading-relaxed">{f.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-slate-500/[0.02] dark:bg-white/[0.01] rounded-3xl p-8">
                            <BarChart className="w-16 h-16 text-amber-500 mb-6" />
                            <h4 className="text-2xl font-bold mb-4 dark:text-white">Smart Retail Analytics</h4>
                            <p className="text-secondary dark:text-gray-300 leading-relaxed">Know which products are hot and which are not. AI-driven demand forecasting helps you restock smarter and increase margins.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-amber-600 dark:bg-accent rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Power your shop with AI</h2>
                    <p className="text-amber-50 mb-8 text-lg relative z-10">Join the retail revolution today.</p>
                    <button className="px-10 py-4 bg-white text-amber-600 dark:text-accent rounded-xl font-bold hover:bg-opacity-90 transition-all flex items-center gap-2 mx-auto relative z-10 shadow-xl">
                        Get Started for Free <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </section>
        </div>
    );
}
